import { ArrowRight } from "lucide-react";
import type { CtaBannerComponent } from "../../types";
import { RuntimeLink } from "../../runtime/RendererRuntime";
import { resolveLinkHref } from "../../utils/links";

/**
 * Call-to-action banner.
 *
 * Two visual variants — picked via `content.variant`:
 *
 *  - **`soft`** (default) — quiet neutral card with a green left bar, ink
 *    heading, inline green text link with a small amber arrow. Lives inside
 *    editorial body flow.
 *
 *  - **`primary`** — dark premium banner: the shared carbon surface with a
 *    green ring, soft signature glow and an inner radial glow anchored
 *    top-right; white Space Grotesk heading and an amber button that lifts on
 *    hover. Used as a section's main call-to-action (e.g. the Espace Pro
 *    banner on the home page).
 */
export function CtaBannerRenderer({ component }: { component: CtaBannerComponent }) {
  const { title, description, linkLabel, linkHref, variant = "soft" } = component.content;
  const resolvedHref = resolveLinkHref(linkHref);

  if (variant === "primary") {
    return (
      <section className="mt-10 mb-6 md:mt-12 md:mb-8">
        <div className="apa-surface-dark apa-edge-glow-top relative overflow-hidden rounded-xl px-5 py-6 ring-1 ring-white/10 shadow-[var(--shadow-e1)] md:px-8 md:py-7">
          <div className="relative flex flex-col gap-4 md:flex-row md:items-center md:justify-between md:gap-8">
            <div className="flex flex-col gap-2 md:max-w-2xl">
              <h3 className="text-lg font-bold tracking-tight text-white md:text-xl" style={{ fontFamily: "var(--font-aleo)" }}>
                {title}
              </h3>
              {description ? (
                <p className="text-sm leading-relaxed text-white/70 md:text-base">{description}</p>
              ) : null}
            </div>
            {resolvedHref && linkLabel ? (
              <RuntimeLink
                href={resolvedHref}
                className="group inline-flex shrink-0 items-center gap-2 self-start rounded-lg bg-[#FFC105] px-6 py-3 text-sm font-bold text-[#0A0A0A] shadow-[var(--shadow-e1)] transition hover:-translate-y-0.5 hover:shadow-[var(--shadow-e2)] focus:outline-none focus:ring-2 focus:ring-white/50 md:self-auto md:text-base"
              >
                {linkLabel}
                <ArrowRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-1" />
              </RuntimeLink>
            ) : null}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section>
      <div className="rounded-r-xl border-l-4 border-[#00B862] bg-[var(--color-surface-1)] px-6 py-5 md:px-8 md:py-6">
        <div className="flex flex-col gap-2">
          <h3 className="text-lg font-semibold text-[#0A0A0A] md:text-xl">{title}</h3>
          {description ? (
            <p className="text-sm md:text-base text-[#121212]/80 leading-relaxed">{description}</p>
          ) : null}
          {resolvedHref && linkLabel ? (
            <RuntimeLink href={resolvedHref} className="group inline-flex items-center gap-2 text-sm font-medium text-[#00935A] xl:text-base">
              {linkLabel}
              <ArrowRight className="h-4 w-4 shrink-0 text-[#FFC105] transition-transform group-hover:translate-x-1" />
            </RuntimeLink>
          ) : null}
        </div>
      </div>
    </section>
  );
}
